// Pemisahan SESI login admin vs storefront (pembeli) vs panel platform.
//
// Supabase menyimpan sesi auth di cookie bernama `sb-<ref>-auth-token`. Bila
// panel admin dan storefront memakai nama cookie yang SAMA, login admin akan
// menimpa login pembeli (dan sebaliknya) — itulah yang dikeluhkan: harus login
// ulang tiap pindah area. Solusinya: beri SESI ADMIN nama cookie/storageKey
// yang berbeda, sehingga kedua sesi hidup berdampingan di satu browser.
//
// Storefront tetap memakai nama default supabase (tidak diubah) agar sesi
// pembeli yang sudah ada tidak ter-logout saat fitur ini dirilis.
//
// Scope KETIGA "platform" (panel /platform/admin di apex kelolify.com)
// memakai cookie terpisah lagi: pemilik platform perlu bisa login di panel
// platform DAN login sebagai admin toko penyewa (akun auth berbeda) di browser
// yang sama saat membantu klien — satu scope bersama membuat login kedua
// menendang yang pertama.

import { isPlatformAuthPath } from "@/lib/platformAdmin";

export type AuthScope = "store" | "admin" | "platform";

/**
 * Nama cookie/storageKey khusus SESI ADMIN. Sengaja berbeda dari default
 * supabase (`sb-<ref>-auth-token`) milik storefront. Diteruskan ke
 * `cookieOptions.name` pada client `@supabase/ssr`, yang otomatis memakai nilai
 * ini sebagai `auth.storageKey` juga (lihat createBrowserClient/createServerClient).
 */
export const ADMIN_AUTH_STORAGE_KEY = "sb-galea-admin-auth-token";

/**
 * Nama cookie/storageKey SESI PANEL PLATFORM (pemilik platform). Nama baru =
 * nol tabrakan dengan sesi admin/pembeli yang sudah hidup di browser mana pun.
 *
 * ⛔ SENGAJA TETAP `mitrahub` sesudah rebranding ke Kelolify (2026-09-11).
 * Nama cookie adalah IDENTITAS TEKNIS, bukan merek: menggantinya tidak
 * mengubah satu piksel pun yang dilihat siapa pun, tapi MELOGOUT setiap sesi
 * panel platform yang sedang hidup. Alasan yang sama menahan prefiks
 * `mitrahub:` di `catalogCart.ts`/`cartActionDraft.ts` (keranjang pembeli yang
 * sedang diisi) dan `mitrahub-platform-visitor-id` di `platformVisitor.ts`
 * (dedup pengunjung — berganti nama = setiap pengunjung lama terhitung baru).
 */
export const PLATFORM_AUTH_STORAGE_KEY = "sb-mitrahub-platform-auth-token";

/**
 * Tentukan scope dari path. Area platform (`/platform/admin*` + `/platform/login`)
 * dicek lebih dulu; lalu semua route panel admin toko (`/admin*`, termasuk
 * `/admin/login`). Sisanya = storefront.
 */
export function scopeForPath(pathname: string): AuthScope {
  if (isPlatformAuthPath(pathname)) return "platform";
  return pathname === "/admin" || pathname.startsWith("/admin/")
    ? "admin"
    : "store";
}
